import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SignIn.css';

function SignIn() {
    const [signInData, setSignInData] = useState({
        emailOrPhone: '',
        password: '',
        location: '',
        deviceName: ''
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (location.state?.message) {
            toast.success(location.state.message, { autoClose: 5000 });
            
            if (location.state?.email) {
                setSignInData(prev => ({
                    ...prev,
                    emailOrPhone: location.state.email
                }));
            }
        }

        setSignInData(prev => ({
            ...prev,
            deviceName: navigator.userAgent
        }));

        const fetchLocation = async () => {
            try {
                const res = await axios.get('https://ipapi.co/json/', { timeout: 5000 });
                setSignInData(prev => ({ 
                    ...prev, 
                    location: res.data.city || 'Unknown' 
                }));
            } catch (error) {
                console.warn('Lokasiya məlumatı əldə edilə bilmədi');
                setSignInData(prev => ({ ...prev, location: 'Unknown' }));
            }
        };

        fetchLocation();
    }, [location.state]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSignInData(prevData => ({ ...prevData, [name]: value }));
        setErrorMessage('');
    };

    const handleSignIn = async (e) => {
        e.preventDefault();

        if (!signInData.emailOrPhone || !signInData.password) {
            setErrorMessage('Bütün xanalara daxil edin!');
            return toast.warn('⚠️ Bütün xanalara daxil edin!');
        }

        setLoading(true);

        try {
            const response = await axios.post(
                'https://api.herracim.az/api/Account/login', 
                signInData, 
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    timeout: 10000
                }
            );

            if (response.status === 200) {
                const { user } = response.data;
                toast.success('🎉 Giriş uğurlu oldu!');

                setTimeout(() => {
                    if (user.role === 'Admin') {
                        navigate('/dashboard');
                    } else {
                        navigate('/');
                    }
                    window.location.reload();
                }, 1500);
            }
        } catch (error) {
            console.error('🚨 Giriş xətası:', error);
            
            if (error.response) {
                const errorData = error.response.data;
                const message = typeof errorData === 'string' 
                    ? errorData 
                    : errorData.message || 'Naməlum xəta';

                if (message.includes('təsdiqlənməyib')) {
                    toast.warning('📵 Telefon nömrəsi təsdiqlənməyib!');
                    setErrorMessage('Telefon nömrəsi təsdiqlənməyib!');
                } else if (message.includes('ban olunub')) {
                    toast.error('🚫 Sizin hesabınız ban olunub!');
                    setErrorMessage('Sizin hesabınız ban olunub!');
                } else if (message.includes('tapılmadı')) {
                    toast.error('❌ İstifadəçi tapılmadı!');
                    setErrorMessage('İstifadəçi tapılmadı!');
                } else if (message.includes('yanlış') || message.includes('Yanlış')) {
                    toast.error('❌ Email və ya şifrə yanlışdır!');
                    setErrorMessage('Email və ya şifrə yanlışdır!');
                } else {
                    toast.error(`❌ ${message}`);
                    setErrorMessage(message);
                }
            } else if (error.code === 'ERR_NETWORK') {
                toast.error('🌐 Serverlə bağlantı qurula bilmədi.');
                setErrorMessage('Serverlə bağlantı qurula bilmədi.');
            } else if (error.code === 'ECONNABORTED') {
                toast.error('⏱️ Sorğu vaxtı bitdi.');
                setErrorMessage('Sorğu vaxtı bitdi.');
            } else {
                toast.error('❌ Gözlənilməz xəta baş verdi.');
                setErrorMessage('Gözlənilməz xəta baş verdi.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='herrac-signin-wrapper'>
            <div className='herrac-signin-overlay'></div>
            
            <div className='herrac-signin-container'>
                <Link to='/' className='herrac-signin-close' aria-label='Bağla'>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </Link>
                
                <div className='herrac-signin-card'>
                    <div className='herrac-signin-header'>
                        <div className='herrac-signin-icon'>
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="22" stroke="url(#gradient1)" strokeWidth="2"/>
                                <path d="M24 14V24L30 30" stroke="url(#gradient1)" strokeWidth="2" strokeLinecap="round"/>
                                <defs>
                                    <linearGradient id="gradient1" x1="0" y1="0" x2="48" y2="48">
                                        <stop offset="0%" stopColor="#667eea"/>
                                        <stop offset="100%" stopColor="#764ba2"/>
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <h2 className='herrac-signin-title'>Kabinetə Giriş</h2>
                        <p className='herrac-signin-subtitle'>Hərrac platformasına xoş gəlmisiniz</p>
                    </div>

                    <form className='herrac-signin-form' onSubmit={handleSignIn} noValidate>
                        <div className='herrac-signin-input-group'>
                            <label className='herrac-signin-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M2.5 5.83333L10 10.8333L17.5 5.83333M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                E-mail və ya mobil nömrə
                            </label>
                            <input
                                type='text'
                                name='emailOrPhone'
                                value={signInData.emailOrPhone}
                                onChange={handleInputChange}
                                placeholder='example@mail.com və ya +994501234567'
                                className='herrac-signin-input'
                                autoComplete='username'
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className='herrac-signin-input-group'>
                            <label className='herrac-signin-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.83333 9.16667V5.83333C5.83333 4.72826 6.27232 3.66846 7.05372 2.88706C7.83512 2.10565 8.89493 1.66667 10 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1667 4.72826 14.1667 5.83333V9.16667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Şifrə
                            </label>
                            <div className='herrac-signin-password-wrapper'>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name='password'
                                    value={signInData.password}
                                    onChange={handleInputChange}
                                    placeholder='••••••••'
                                    className='herrac-signin-input'
                                    autoComplete='current-password'
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type='button'
                                    className='herrac-signin-password-toggle'
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M8.33333 8.33333C8.11232 8.65885 7.99609 9.04836 8.00008 9.44605C8.00407 9.84373 8.12806 10.2306 8.35531 10.5515C8.58256 10.8723 8.90151 11.1116 9.2695 11.2372C9.63749 11.3627 10.0346 11.3683 10.4058 11.2533M11.6667 11.6667C11.0844 12.0992 10.3609 12.2822 9.64002 12.178C8.91916 12.0739 8.26431 11.6903 7.80834 11.1066C7.35237 10.5229 7.13046 9.78413 7.19185 9.04086C7.25325 8.29759 7.59299 7.60648 8.14167 7.1M14.95 14.95C13.5255 16.0358 11.788 16.6374 10 16.6667C5 16.6667 2.5 10 2.5 10C3.45651 8.06825 4.81629 6.38051 6.475 5.05L14.95 14.95ZM8.25833 3.53333C8.82361 3.39907 9.4009 3.33195 9.98 3.33333C14.98 3.33333 17.4808 10 17.4808 10C17.0535 10.9463 16.5269 11.8423 15.9108 12.6742L8.25833 3.53333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {errorMessage && (
                            <div className='herrac-signin-error' role='alert'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 6.66667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 13.3333H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {errorMessage}
                            </div>
                        )}

                        <button 
                            type='submit' 
                            className='herrac-signin-button' 
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className='herrac-signin-spinner'></span>
                                    Giriş edilir...
                                </>
                            ) : (
                                <>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M7.5 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H7.5M13.3333 14.1667L17.5 10M17.5 10L13.3333 5.83333M17.5 10H7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Daxil ol
                                </>
                            )}
                        </button>
                    </form>
                    
                    <div className='herrac-signin-divider'>
                        <span>və ya</span>
                    </div>

                    <div className='herrac-signin-footer'>
                        <p className='herrac-signin-footer-text'>
                            Hələ qeydiyyatdan keçməmisiniz?
                        </p>
                        <Link to='/qeydiyyat' className='herrac-signin-footer-link'>
                            Yeni hesab yarat
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </Link>
                        <Link to='/sifreni-unutdum' className='herrac-signin-forgot-link'>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M12.6667 7.33333H3.33333C2.59695 7.33333 2 7.93029 2 8.66667V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V8.66667C14 7.93029 13.403 7.33333 12.6667 7.33333Z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M4.66667 7.33333V4.66667C4.66667 3.78261 5.01786 2.93476 5.64298 2.30964C6.2681 1.68452 7.11595 1.33333 8 1.33333C8.88406 1.33333 9.7319 1.68452 10.357 2.30964C10.9821 2.93476 11.3333 3.78261 11.3333 4.66667V7.33333" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Şifrəni unutmusunuz?
                        </Link>
                    </div>
                </div>
            </div>

            <ToastContainer
                position='top-center'
                autoClose={3000}
                hideProgressBar={false}
                closeOnClick
                pauseOnHover
                theme='light'
                style={{ zIndex: 99999 }}
            />
        </div>
    );
}

export default SignIn;